import React from "react";
import { SpinnerIcon } from "@/components/icons";
import { LOGIN_TEXT } from "../loginStyles";
import { LoginBackground } from "./LoginBackground";
import benerdyMark from "@/assets/benerdy-mark.png";

/**
 * The page every sign-in screen shares: the website's mesh behind, the mark
 * in a clear bubble, and the step in a glass card. `footer` sits apart from
 * the card at the bottom of the screen, clear of the home indicator.
 *
 * The page is at least the visible height (`dvh`, not iOS's toolbar-less `vh`).
 * `--login-gap` is the space between sections: 32px where the screen is tall
 * enough, shrinking to 16px so the first step fits an iPhone in Safari.
 */
export const LoginPage: React.FC<{ children: React.ReactNode; footer?: React.ReactNode }> = ({
  children,
  footer,
}) => (
  <div className="relative min-h-dvh flex flex-col [--login-gap:clamp(16px,3.5dvh,32px)] px-[16px] pt-[max(16px,env(safe-area-inset-top))] pb-[max(16px,env(safe-area-inset-bottom))]">
    <LoginBackground />
    <main className="relative z-10 flex-1 flex items-center justify-center">
      <div className="max-w-[420px] w-full">
        {/* The logo sits on the background in a clear bubble, so the animation shows behind it. */}
        <div className="mb-(--login-gap) w-[96px] h-[96px] rounded-full bg-white/25 ring-1 ring-white/70 backdrop-blur-[2px] flex items-center justify-center">
          <img src={benerdyMark} alt="BeNerdy" className="h-[56px] w-auto" />
        </div>
        <div className="bg-white/60 backdrop-blur-xl rounded-2xl px-[20px] py-(--login-gap) min-[380px]:px-[32px]">{children}</div>
      </div>
    </main>
    {footer && <footer className="relative z-10 w-full max-w-[420px] mx-auto pt-(--login-gap)">{footer}</footer>}
  </div>
);

/** A step that is waiting on the server: one spinner and what is happening (DD-001). */
export const LoginPending: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center gap-[12px]" role="status">
    <SpinnerIcon className="w-[24px] h-[24px] text-gray-500 shrink-0" />
    <p className={LOGIN_TEXT}>{label}</p>
  </div>
);
